import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icons from "../icons";
import CreateTestCasePage from "./CreateTestCasePage";
import ManualTestCasePage from "./ManualTestCasePage";
import ExecutionReportModal from "./ExecutionReportModal";
import CreateExecutionWizard from "./CreateExecutionWizard";
import LinkExecutionModal from "./LinkExecutionModal";
import ConfirmModal from "./ConfirmModal";
import CreateTestsModal from "./CreateTestsModal";
import HelpButton from "./HelpDialog";
import { DIALOG_HEIGHT } from "./dialogSize";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

// This page intentionally shows only tickets in this workflow status.
const STATUS_FILTER = "Testing";

/* Map a Jira status category to a badge colour. */
function statusBadgeClasses(category) {
  switch ((category || "").toLowerCase()) {
    case "done":
      return "bg-green-50 text-green-700 ring-green-600/20";
    case "indeterminate": // In Progress / Testing
      return "bg-blue-50 text-blue-700 ring-blue-600/20";
    default: // "new" / To Do / unknown
      return "bg-slate-100 text-slate-600 ring-slate-500/20";
  }
}

/* Map a Jira priority name to a small dot colour. */
function priorityDotClass(priority) {
  switch ((priority || "").toLowerCase()) {
    case "highest":
    case "high":
      return "bg-red-500";
    case "medium":
      return "bg-amber-500";
    case "low":
    case "lowest":
      return "bg-slate-400";
    default:
      return "bg-slate-300";
  }
}

/* Rich priority styling for the ticket cards (dot + pill colours + label). */
function priorityMeta(priority) {
  switch ((priority || "").toLowerCase()) {
    case "highest":
      return {
        label: "Highest",
        dot: "bg-red-500",
        pill: "bg-red-50 text-red-700 ring-red-600/20",
      };
    case "high":
      return {
        label: "High",
        dot: "bg-orange-500",
        pill: "bg-orange-50 text-orange-700 ring-orange-600/20",
      };
    case "medium":
      return {
        label: "Medium",
        dot: "bg-amber-500",
        pill: "bg-amber-50 text-amber-700 ring-amber-600/20",
      };
    case "low":
      return {
        label: "Low",
        dot: "bg-sky-500",
        pill: "bg-sky-50 text-sky-700 ring-sky-600/20",
      };
    case "lowest":
      return {
        label: "Lowest",
        dot: "bg-slate-400",
        pill: "bg-slate-50 text-slate-600 ring-slate-500/20",
      };
    default:
      return {
        label: priority || "None",
        dot: "bg-slate-300",
        pill: "bg-slate-50 text-slate-600 ring-slate-500/20",
      };
  }
}

/* "2026-09-04T13:13:54.000+0530" -> "Sep 4, 2026". Falls back to raw string. */
function formatDate(raw) {
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Modal to associate a Testing ticket with one board and link/unlink the
 * board's test cases to it. Mirrors the ticket-links API:
 *   1. POST /api/ticket-links  -> ensure the local anchor + set its board
 *   2. PUT  /api/ticket-links/{ticketkey}/testcases -> replace linked set
 */
function TicketManageModal({ ticket, projectKey, onClose, onSaved }) {
  const [boards, setBoards] = useState([]);
  const [boardId, setBoardId] = useState("");
  const [cases, setCases] = useState([]);
  const [checked, setChecked] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [casesLoading, setCasesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // On open: load boards + any existing anchor (board + linked cases) for this key.
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [bRes, tRes] = await Promise.all([
          fetch(`${API_BASE_URL}boards`),
          fetch(`${API_BASE_URL}ticket-links`),
        ]);
        const bBody = await bRes.json().catch(() => ({}));
        const tBody = await tRes.json().catch(() => ({}));
        if (!active) return;
        const boardList = Array.isArray(bBody?.data) ? bBody.data : [];
        setBoards(boardList);

        const existing = (Array.isArray(tBody?.data) ? tBody.data : []).find(
          (a) => a.ticket_key === ticket.key
        );
        let initialBoard = existing?.board_id || "";
        if (existing?.ticketkey) {
          const dRes = await fetch(
            `${API_BASE_URL}ticket-links/${encodeURIComponent(existing.ticketkey)}`
          );
          const dBody = await dRes.json().catch(() => ({}));
          if (active && dBody?.data) {
            initialBoard = dBody.data.board_id || initialBoard;
            setChecked(new Set((dBody.data.tests || []).map((t) => t.testcasekey)));
          }
        }
        setBoardId(initialBoard);
      } catch {
        if (active) setError("Failed to load test suites / existing links.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [ticket.key]);

  // Load the selected board's test cases whenever the board changes.
  useEffect(() => {
    if (!boardId) {
      setCases([]);
      return;
    }
    let active = true;
    setCasesLoading(true);
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}testcases?board_id=${encodeURIComponent(boardId)}`
        );
        const body = await res.json().catch(() => ({}));
        if (active) setCases(Array.isArray(body?.data) ? body.data : []);
      } catch {
        if (active) setCases([]);
      } finally {
        if (active) setCasesLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [boardId]);

  const toggle = (key) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const save = async () => {
    if (!boardId) {
      setError("Pick a test suite to tie this work item to.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const upRes = await fetch(`${API_BASE_URL}ticket-links`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ticket_key: ticket.key,
          project_key: projectKey || "",
          epic_key: ticket.epic_key || "",
          summary: ticket.summary || "",
          status: ticket.status || "",
          issue_type: ticket.issue_type || "",
          priority: ticket.priority || "",
          url: ticket.url || "",
          board_id: boardId,
        }),
      });
      const upBody = await upRes.json().catch(() => ({}));
      if (!upRes.ok || !upBody?.data?.ticketkey) {
        throw new Error(upBody?.detail || `HTTP ${upRes.status}`);
      }
      const ticketkey = upBody.data.ticketkey;

      const linkRes = await fetch(
        `${API_BASE_URL}ticket-links/${encodeURIComponent(ticketkey)}/testcases`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ testcase_keys: Array.from(checked) }),
        }
      );
      if (!linkRes.ok) {
        const b = await linkRes.json().catch(() => ({}));
        throw new Error(b?.detail || `HTTP ${linkRes.status}`);
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className={`flex ${DIALOG_HEIGHT} w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-200`}>
        <div className="flex flex-none items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900">
              Link tests &amp; board
            </h2>
            <p className="mt-0.5 truncate text-sm text-slate-500">
              <span className="font-mono font-medium text-brand-700">{ticket.key}</span>{" "}
              {ticket.summary}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-none rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Icons.Spinner className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : (
            <>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-slate-700">Test Suite</span>
                <select
                  value={boardId}
                  onChange={(e) => setBoardId(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                >
                  <option value="">Select a test suite…</option>
                  {boards.map((b) => (
                    <option key={b.board_id} value={b.board_id}>
                      {b.name}
                    </option>
                  ))}
                </select>
                <span className="mt-1 block text-xs text-slate-400">
                  New test cases created from this work item inherit this test suite.
                </span>
              </label>

              <div>
                <p className="mb-1 text-sm font-medium text-slate-700">
                  Link existing test cases
                </p>
                {!boardId ? (
                  <p className="rounded-lg bg-slate-50 px-3 py-4 text-center text-sm text-slate-400">
                    Pick a test suite to see its test cases.
                  </p>
                ) : casesLoading ? (
                  <div className="flex items-center gap-2 px-1 py-3 text-sm text-slate-500">
                    <Icons.Spinner className="h-4 w-4 animate-spin" />
                    Loading test cases…
                  </div>
                ) : !cases.length ? (
                  <p className="rounded-lg bg-slate-50 px-3 py-4 text-center text-sm text-slate-400">
                    This test suite has no test cases yet. Create one and pick this work
                    item in the &ldquo;link to a Testing work item?&rdquo; step.
                  </p>
                ) : (
                  <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200">
                    <ul className="divide-y divide-slate-100">
                      {cases.map((c) => (
                        <li key={c.testcasekey}>
                          <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50">
                            <input
                              type="checkbox"
                              checked={checked.has(c.testcasekey)}
                              onChange={() => toggle(c.testcasekey)}
                              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                            />
                            <span className="min-w-0 flex-1 truncate text-slate-800">
                              {c.name}
                            </span>
                            <span className="flex-none text-xs text-slate-400">
                              {c.status}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}
            </>
          )}
        </div>

        <div className="flex flex-none items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || loading}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-60"
          >
            {saving && <Icons.Spinner className="h-4 w-4 animate-spin" />}
            Save links
          </button>
        </div>
      </div>
    </div>
  );
}


/* Initials for an assignee avatar ("Alan Riley" -> "AR"). */
function initials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/* Pill colours for a test-case result status. */
function tcStatusClasses(status) {
  switch ((status || "").toLowerCase()) {
    case "passed":
      return "bg-green-50 text-green-700 ring-green-600/20";
    case "failed":
      return "bg-red-50 text-red-700 ring-red-600/20";
    default:
      return "bg-slate-100 text-slate-500 ring-slate-500/20";
  }
}

/* Pill colours for an execution status (draft | in_progress | completed). */
function execStatusClasses(status) {
  switch ((status || "").toLowerCase()) {
    case "completed":
      return "bg-green-50 text-green-700 ring-green-600/20";
    case "in_progress":
      return "bg-blue-50 text-blue-700 ring-blue-600/20";
    default:
      return "bg-slate-100 text-slate-500 ring-slate-500/20";
  }
}

/* Custom Epic dropdown. A native <select> popup always sizes to the widest
   option and cannot wrap text, so long epic titles blow the width out. This
   replacement keeps a fixed control/panel width and wraps long titles onto
   multiple lines. Closes on outside-click or Escape. */
function EpicSelect({ value, onChange, epics, loading }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = epics.find((e) => e.key === value) || null;
  const disabled = loading || !epics.length;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const label = loading
    ? "Loading epics…"
    : selected
    ? `${selected.key} — ${selected.summary}`
    : epics.length
    ? "Select an epic…"
    : "No epics in this project";

  return (
    <div className="relative w-72" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-start justify-between gap-2 rounded-lg border border-slate-200 bg-card px-3 py-2 text-left text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-60"
      >
        <span className={`whitespace-normal break-words ${selected ? "" : "text-slate-400"}`}>
          {label}
        </span>
        <Icons.ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
      </button>
      {open && !disabled && (
        <div className="absolute left-0 z-30 mt-1 max-h-72 w-72 overflow-auto rounded-lg border border-slate-200 bg-card py-1 shadow-lg">
          <button
            type="button"
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            className="block w-full px-3 py-2 text-left text-sm text-slate-500 hover:bg-slate-50"
          >
            Select an epic…
          </button>
          {epics.map((ep) => (
            <button
              key={ep.key}
              type="button"
              onClick={() => {
                onChange(ep.key);
                setOpen(false);
              }}
              className={`block w-full whitespace-normal break-words px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                ep.key === value ? "bg-brand-50 text-brand-800" : "text-slate-700"
              }`}
            >
              <span className="font-mono font-semibold">{ep.key}</span> — {ep.summary}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* A "+" add menu that opens a small dropdown of add actions. Used in the
   Tests and Executions section headers to hold Create / Add options. */
function AddMenu({ items, label = "Add" }) {
  const [open, setOpen] = useState(false);

  const act = (fn) => () => {
    setOpen(false);
    fn?.();
  };

  return (
    <div className="relative flex-none">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={label}
        title={label}
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600 text-white shadow-sm transition hover:bg-brand-700"
      >
        <Icons.Plus className="h-5 w-5" strokeWidth={2.5} />
      </button>
      {open && (
        <>
          {/* Click-away backdrop. */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-50 mt-1 w-44 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
            {items.map((it) => (
              <button
                key={it.label}
                type="button"
                onClick={act(it.onClick)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                {it.Icon && <it.Icon className="h-3.5 w-3.5 text-slate-500" />}
                {it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* A kebab actions menu for a linked test-case row: Delink. */
function TestActionsMenu({ onDelink }) {
  const [open, setOpen] = useState(false);

  const act = (fn) => () => {
    setOpen(false);
    fn?.();
  };

  return (
    <div className="relative flex-none">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Test actions"
        title="Actions"
        className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
      >
        <Icons.More className="h-4 w-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-50 mt-1 w-40 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
            <button
              type="button"
              onClick={act(onDelink)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
            >
              <Icons.Unlink className="h-3.5 w-3.5 text-amber-600" />
              Delink
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* A kebab actions menu for an execution row: Run, Delink, Delete. */
function ExecutionActionsMenu({ onRun, onDelink, onDelete }) {
  const [open, setOpen] = useState(false);

  const act = (fn) => () => {
    setOpen(false);
    fn?.();
  };

  return (
    <div className="relative flex-none">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Execution actions"
        title="Actions"
        className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
      >
        <Icons.More className="h-4 w-4" />
      </button>
      {open && (
        <>
          {/* Click-away backdrop. */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-50 mt-1 w-40 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
            <button
              type="button"
              onClick={act(onRun)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
            >
              <Icons.Play className="h-3.5 w-3.5 text-emerald-600" />
              Run
            </button>
            <button
              type="button"
              onClick={act(onDelink)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
            >
              <Icons.Unlink className="h-3.5 w-3.5 text-amber-600" />
              Delink
            </button>
            <button
              type="button"
              onClick={act(onDelete)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-rose-600 hover:bg-rose-50"
            >
              <Icons.Trash className="h-3.5 w-3.5" />
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* The ticket detail page: ticket info + Tests + Executions, shown after a card
   is clicked. */
function TicketDetail({
  ticket,
  det,
  count,
  execCount,
  onCreateTests,
  onLinkTests,
  onDelinkTest,
  onCreateExecution,
  onLinkExecution,
  onViewReport,
  onEditReportTypes,
  onRunExecution,
  onDelinkExecution,
  onDeleteExecution,
  onOpenTestCase,
}) {
  const t = ticket;
  const meta = [
    ["Type", t.issue_type || "—"],
    ["Priority", t.priority || "—"],
    ["Assignee", t.assignee || "Unassigned"],
    ["Created", formatDate(t.created)],
  ];
  if (t.reporter) meta.push(["Reporter", t.reporter]);

  return (
    <div className="space-y-8">
      {/* ---- Ticket details: single row header ---- */}
      <div className="border-b border-slate-200 pb-5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <a
            href={t.url}
            target="_blank"
            rel="noreferrer"
            className="font-mono text-sm font-semibold text-brand-700 hover:underline"
          >
            {t.key}
          </a>
          <span
            className={[
              "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset",
              statusBadgeClasses(t.status_category),
            ].join(" ")}
          >
            {t.status || "—"}
          </span>
          <span className="hidden h-4 w-px bg-slate-200 sm:block" />
          <h1 className="min-w-0 text-lg font-semibold tracking-tight text-slate-900">
            {t.summary}
          </h1>
        </div>
        <dl className="mt-4 flex flex-wrap items-center gap-x-10 gap-y-3">
          {meta.map(([label, value]) => (
            <div key={label} className="flex items-baseline gap-2">
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                {label}
              </dt>
              <dd className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                {label === "Priority" && (
                  <span
                    className={[
                      "h-2 w-2 rounded-full",
                      priorityDotClass(t.priority),
                    ].join(" ")}
                  />
                )}
                {value}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      {/* ---- Two columns: Tests | Executions ---- */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Tests */}
        <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
          <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              Tests
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
                {count}
              </span>
            </h3>
            <AddMenu
              label="Add tests"
              items={[
                { label: "Create tests", Icon: Icons.Plus, onClick: onCreateTests },
                { label: "Add existing tests", Icon: Icons.Boards, onClick: onLinkTests },
              ]}
            />
          </div>
          {det?.loading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
              <Icons.Spinner className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : !det?.tests?.length ? (
            <p className="py-8 text-center text-sm text-slate-400">
              No test cases linked yet.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {det.tests.map((tc) => (
                <li
                  key={tc.testcasekey}
                  className="flex items-center justify-between gap-4 py-3"
                >
                  {tc.board_id ? (
                    <button
                      type="button"
                      onClick={() => onOpenTestCase?.(tc.board_id, tc.testcasekey)}
                      className="min-w-0 flex-1 text-left text-sm font-medium text-slate-800 transition hover:text-brand-700"
                    >
                      {tc.name}
                    </button>
                  ) : (
                    <span className="min-w-0 flex-1 text-sm font-medium text-slate-800">
                      {tc.name}
                    </span>
                  )}
                  <div className="flex flex-none items-center gap-2">
                    <span
                      className={[
                        "rounded-full px-2.5 py-0.5 text-[11px] font-medium capitalize ring-1 ring-inset",
                        tcStatusClasses(tc.status),
                      ].join(" ")}
                    >
                      {tc.status || "pending"}
                    </span>
                    <TestActionsMenu onDelink={() => onDelinkTest?.(tc)} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Executions */}
        <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
          <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              Executions
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
                {execCount}
              </span>
            </h3>
            <AddMenu
              label="Add execution"
              items={[
                {
                  label: "Create execution",
                  Icon: Icons.Plus,
                  onClick: onCreateExecution,
                },
                {
                  label: "Add existing execution",
                  Icon: Icons.Play,
                  onClick: onLinkExecution,
                },
              ]}
            />
          </div>
          {det?.loading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
              <Icons.Spinner className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : !det?.executions?.length ? (
            <p className="py-8 text-center text-sm text-slate-400">
              No executions linked yet. Create one on the Executions page and link
              this work item.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {det.executions.map((ex) => (
                <li
                  key={ex.executionkey}
                  className="flex items-center justify-between gap-4 py-3"
                >
                  <span className="min-w-0 flex-1 text-sm font-medium text-slate-800">
                    {ex.name}
                  </span>
                  <div className="flex flex-none items-center gap-2">
                    <span
                      className={[
                        "rounded-full px-2.5 py-0.5 text-[11px] font-medium capitalize ring-1 ring-inset",
                        execStatusClasses(ex.status),
                      ].join(" ")}
                    >
                      {(ex.status || "").replace("_", " ")}
                    </span>
                    {["in_progress", "completed"].includes((ex.status || "").toLowerCase()) && (
                      <>
                        <button
                          type="button"
                          onClick={() => onViewReport?.(ex)}
                          title="View report"
                          className="rounded-md p-1.5 text-slate-400 transition hover:bg-brand-50 hover:text-brand-600"
                        >
                          <Icons.Eye className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onEditReportTypes?.(ex)}
                          title="Change report types"
                          className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                        >
                          <Icons.Pencil className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                    <ExecutionActionsMenu
                      onRun={() => onRunExecution?.(ex)}
                      onDelink={() => onDelinkExecution?.(ex)}
                      onDelete={() => onDeleteExecution?.(ex)}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}


export default function TicketsView({ onOpenTestCase, userkey, onRunExecution }) {
  const [status, setStatus] = useState(null); // { connected, base_url, display_name, email }
  const [statusLoading, setStatusLoading] = useState(true);

  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState("");

  const [epics, setEpics] = useState([]);
  const [epicsLoading, setEpicsLoading] = useState(false);
  const [selectedEpic, setSelectedEpic] = useState("");

  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  // Ticket row currently open in the link/board modal.
  const [manageTicket, setManageTicket] = useState(null);
  // Ticket row currently open in the "Create tests" wizard.
  const [createTicket, setCreateTicket] = useState(null);
  // Active "create execution" wizard flow (opens the wizard at the review step
  // with the ticket's tests pre-selected + the ticket pre-linked).
  // Gated "create execution": { ticket, loading } while checking, then either
  // { ticket, ready } / { ticket, message } to explain a refusal, or null once we
  // hand off to the wizard via execFlow.
  const [execGate, setExecGate] = useState(null);
  // Active wizard launched from a ticket, with its tests pre-selected.
  const [execFlow, setExecFlow] = useState(null);
  // Execution currently open in the "View report" modal (pick report types).
  const [reportExecution, setReportExecution] = useState(null);
  // "view" = first-click flow (direct open if types saved, else pick);
  // "edit" = always show the type picker to change saved types.
  const [reportMode, setReportMode] = useState("view");
  // Ticket currently open in the "Link execution" modal.
  const [linkExecTicket, setLinkExecTicket] = useState(null);
  // Pending destructive confirmation:
  //   { title, message, confirmLabel, busyLabel, action: async () => void }
  const [confirmState, setConfirmState] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  // Active creation flow launched from the wizard:
  //   { mode:"ai"|"manual", board:{id,name}, subcategorykey, presetTickets }
  const [createFlow, setCreateFlow] = useState(null);
  // Jira key -> { ticketkey, testcase_count, board_id } for linked anchors.
  const [linkMap, setLinkMap] = useState({});
  // Ticket currently opened into its detail page (null = show the card grid).
  const [selectedTicket, setSelectedTicket] = useState(null);
  // Jira key -> { loading, tests: [...], executions: [...] } cache.
  const [detailByKey, setDetailByKey] = useState({});

  // Lazily fetch a ticket's linked test cases + executions once, from the
  // ticket-links detail endpoint (which returns both).
  const ensureDetail = useCallback(
    (jiraKey) => {
      const ticketkey = linkMap[jiraKey]?.ticketkey;
      if (!ticketkey || detailByKey[jiraKey]) return;
      setDetailByKey((prev) => ({
        ...prev,
        [jiraKey]: { loading: true, tests: [], executions: [] },
      }));
      (async () => {
        try {
          const res = await fetch(
            `${API_BASE_URL}ticket-links/${encodeURIComponent(ticketkey)}`
          );
          const body = await res.json().catch(() => ({}));
          const tests = Array.isArray(body?.data?.tests) ? body.data.tests : [];
          const executions = Array.isArray(body?.data?.executions)
            ? body.data.executions
            : [];
          setDetailByKey((prev) => ({
            ...prev,
            [jiraKey]: { loading: false, tests, executions },
          }));
        } catch {
          setDetailByKey((prev) => ({
            ...prev,
            [jiraKey]: { loading: false, tests: [], executions: [] },
          }));
        }
      })();
    },
    [linkMap, detailByKey]
  );

  // --- Connection status -----------------------------------------------------
  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}jira/status`);
      const body = await res.json().catch(() => ({}));
      setStatus(body?.data || { connected: false });
    } catch {
      setStatus({ connected: false, email: "Network error while contacting the server." });
    } finally {
      setStatusLoading(false);
    }
  }, []);

  // --- Linked test-case counts (per Jira key) --------------------------------
  const loadLinks = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}ticket-links`);
      const body = await res.json().catch(() => ({}));
      const list = Array.isArray(body?.data) ? body.data : [];
      const map = {};
      for (const a of list) {
        map[a.ticket_key] = {
          ticketkey: a.ticketkey,
          testcase_count: a.testcase_count || 0,
          execution_count: a.execution_count || 0,
          board_id: a.board_id || null,
        };
      }
      setLinkMap(map);
    } catch {
      /* leave existing map */
    }
  }, []);

  // Launch the gated "create execution" dialog for a ticket. The dialog asks the
  // server whether the ticket is executable at all — it needs at least one
  // linked Xray Test with a Playwright test case, because only that chain can
  // report results and step screenshots back to Jira. The server also picks the
  // test cases and creates the matching Xray Test Execution, so nothing here
  // needs to pre-select or pre-link anything.
  // "Create execution" from a ticket: check readiness first, then hand off to the
  // normal execution wizard with the ticket's automated tests pre-selected and
  // the picker scoped to that ticket. A ticket with no Xray Tests — or none of
  // them automated — never reaches the wizard, because the resulting cycle
  // couldn't report anything back to Jira.
  const startExecution = useCallback(
    async (t) => {
      setExecGate({ ticket: t, loading: true });
      let ticketkey = linkMap[t.key]?.ticketkey || null;
      try {
        const res = await fetch(
          `${API_BASE_URL}coverage/execution-readiness?ticket=${encodeURIComponent(t.key)}`,
        );
        const body = await res.json().catch(() => ({}));
        const ready = body?.data || null;
        if (!res.ok || !ready) {
          setExecGate({
            ticket: t,
            loading: false,
            message: body?.detail || `Could not check this work item (HTTP ${res.status}).`,
          });
          return;
        }
        if (!ready.can_create) {
          setExecGate({ ticket: t, loading: false, ready });
          return;
        }

        // Ensure the local anchor so the execution can be linked to the ticket.
        if (!ticketkey) {
          const aRes = await fetch(`${API_BASE_URL}ticket-links`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ticket_key: t.key,
              project_key: selectedProject || "",
              epic_key: t.epic_key || "",
              summary: t.summary || "",
              status: t.status || "",
              issue_type: t.issue_type || "",
              priority: t.priority || "",
              url: t.url || "",
            }),
          });
          const aBody = await aRes.json().catch(() => ({}));
          if (aRes.ok && aBody?.data?.ticketkey) ticketkey = aBody.data.ticketkey;
        }

        // Pre-select every automated case the gate found for this ticket.
        const selected = {};
        for (const e of ready.eligible || []) {
          selected[e.testcasekey] = {
            name: e.name,
            board_id: e.board_id,
            boardName: e.board_name,
            priority: e.priority,
          };
        }

        setExecGate(null);
        setExecFlow({
          name: `${t.key} — execution`,
          description: `Automated tests for ${t.key}: ${ready.summary || t.summary || ""}`.trim(),
          selected,
          linkTickets: ticketkey
            ? [{ ticketkey, ticket_key: t.key, summary: t.summary || "" }]
            : [],
          scopeTicketKeys: [t.key],
          step: 0,
        });
      } catch (e) {
        setExecGate({ ticket: t, loading: false, message: `Could not check this work item: ${e.message}` });
      }
    },
    [linkMap, selectedProject],
  );

  // Refresh a ticket's linked tests/executions + the per-key count map after a
  // mutation (delete / delink) so the detail view reflects the change.
  const refreshTicketDetail = useCallback(
    (jiraKey) => {
      loadLinks();
      setDetailByKey((prev) => {
        const next = { ...prev };
        delete next[jiraKey];
        return next;
      });
    },
    [loadLinks]
  );

  // Delete an execution outright (removes it everywhere, not just this ticket).
  const deleteExecution = useCallback(
    async (ticket, ex) => {
      const res = await fetch(
        `${API_BASE_URL}executions/${encodeURIComponent(ex.executionkey)}`,
        { method: "DELETE" }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      refreshTicketDetail(ticket.key);
    },
    [refreshTicketDetail]
  );

  // Delink an execution from this ticket only (the execution itself is kept).
  const delinkExecution = useCallback(
    async (ticket, ex) => {
      const ticketkey = linkMap[ticket.key]?.ticketkey;
      if (!ticketkey) throw new Error("This ticket has no local link record yet.");
      const res = await fetch(
        `${API_BASE_URL}ticket-links/${encodeURIComponent(
          ticketkey
        )}/executions/${encodeURIComponent(ex.executionkey)}`,
        { method: "DELETE" }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      refreshTicketDetail(ticket.key);
    },
    [linkMap, refreshTicketDetail]
  );

  // Delink a test case from this ticket (the test case itself is kept).
  const delinkTest = useCallback(
    async (ticket, tc) => {
      const ticketkey = linkMap[ticket.key]?.ticketkey;
      if (!ticketkey) throw new Error("This ticket has no local link record yet.");
      const res = await fetch(
        `${API_BASE_URL}ticket-links/${encodeURIComponent(
          ticketkey
        )}/testcases/${encodeURIComponent(tc.testcasekey)}`,
        { method: "DELETE" }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      refreshTicketDetail(ticket.key);
    },
    [linkMap, refreshTicketDetail]
  );

  // Run the pending destructive action from the shared confirm modal.
  const runConfirm = useCallback(async () => {
    if (!confirmState?.action) return;
    setConfirmBusy(true);
    try {
      await confirmState.action();
      setConfirmState(null);
    } catch (e) {
      alert(e.message || "Action failed.");
      setConfirmState(null);
    } finally {
      setConfirmBusy(false);
    }
  }, [confirmState]);

  // Ensure a ticket has a local anchor row, returning its ticketkey (creating
  // it if needed). Used before linking executions to a ticket.
  const ensureAnchor = useCallback(
    async (t) => {
      let ticketkey = linkMap[t.key]?.ticketkey || null;
      if (ticketkey) return ticketkey;
      const res = await fetch(`${API_BASE_URL}ticket-links`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ticket_key: t.key,
          project_key: selectedProject || "",
          epic_key: t.epic_key || "",
          summary: t.summary || "",
          status: t.status || "",
          issue_type: t.issue_type || "",
          priority: t.priority || "",
          url: t.url || "",
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok && body?.data?.ticketkey) ticketkey = body.data.ticketkey;
      return ticketkey;
    },
    [linkMap, selectedProject]
  );

  // Link existing executions to a ticket (ensures the anchor, then persists).
  const linkExecutions = useCallback(
    async (ticket, executionKeys) => {
      const ticketkey = await ensureAnchor(ticket);
      if (!ticketkey) throw new Error("Could not create the ticket link record.");
      const res = await fetch(
        `${API_BASE_URL}ticket-links/${encodeURIComponent(ticketkey)}/executions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ execution_keys: executionKeys }),
        }
      );
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail || `HTTP ${res.status}`);
      }
      refreshTicketDetail(ticket.key);
    },
    [ensureAnchor, refreshTicketDetail]
  );

  // --- Projects --------------------------------------------------------------
  const loadProjects = useCallback(async () => {
    setProjectsLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}jira/projects`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body?.detail || "Failed to load Jira projects.");
        setProjects([]);
        return;
      }
      const list = Array.isArray(body?.data) ? body.data : [];
      setProjects(list);
      if (list.length) setSelectedProject((cur) => cur || list[0].key);
    } catch {
      setError("Network error while loading Jira projects.");
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  // --- Epics for the selected project ----------------------------------------
  const loadEpics = useCallback(async (projectKey) => {
    if (!projectKey) {
      setEpics([]);
      return;
    }
    setEpicsLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${API_BASE_URL}jira/epics?project=${encodeURIComponent(projectKey)}`
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body?.detail || "Failed to load epics for this project.");
        setEpics([]);
        return;
      }
      setEpics(Array.isArray(body?.data) ? body.data : []);
    } catch {
      setError("Network error while loading epics.");
      setEpics([]);
    } finally {
      setEpicsLoading(false);
    }
  }, []);

  // --- Testing tickets under the selected epic -------------------------------
  const loadTickets = useCallback(async (projectKey, epicKey) => {
    if (!projectKey || !epicKey) {
      setTickets([]);
      return;
    }
    setTicketsLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${API_BASE_URL}jira/tickets?project=${encodeURIComponent(projectKey)}` +
          `&epic=${encodeURIComponent(epicKey)}` +
          `&status=${encodeURIComponent(STATUS_FILTER)}&max_results=100`
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body?.detail || "Failed to load work items for this epic.");
        setTickets([]);
        return;
      }
      setTickets(Array.isArray(body?.data?.issues) ? body.data.issues : []);
    } catch {
      setError("Network error while loading work items.");
      setTickets([]);
    } finally {
      setTicketsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadLinks();
  }, [loadStatus, loadLinks]);

  useEffect(() => {
    if (status?.connected) loadProjects();
  }, [status?.connected, loadProjects]);

  // When the project changes, reset epic + tickets and reload the epic list.
  useEffect(() => {
    setSelectedEpic("");
    setTickets([]);
    setSelectedTicket(null);
    if (selectedProject) loadEpics(selectedProject);
  }, [selectedProject, loadEpics]);

  // When the epic changes, load its Testing tickets.
  useEffect(() => {
    if (selectedProject && selectedEpic) loadTickets(selectedProject, selectedEpic);
    else setTickets([]);
  }, [selectedProject, selectedEpic, loadTickets]);

  // When a ticket is opened (or its cached detail is cleared after a save),
  // fetch its linked tests + executions for the detail page.
  useEffect(() => {
    if (selectedTicket) ensureDetail(selectedTicket.key);
  }, [selectedTicket, ensureDetail]);

  const selectedEpicObj = useMemo(
    () => epics.find((e) => e.key === selectedEpic) || null,
    [epics, selectedEpic]
  );

  const filteredTickets = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tickets;
    return tickets.filter(
      (t) =>
        t.key.toLowerCase().includes(q) ||
        (t.summary || "").toLowerCase().includes(q) ||
        (t.assignee || "").toLowerCase().includes(q)
    );
  }, [tickets, query]);

  // --- Report helpers --------------------------------------------------------

  // "View report" (eye icon): if report_types already saved on the execution,
  // directly generate the report and open the PDF. Otherwise show the picker.
  const handleViewReport = useCallback(
    async (ex) => {
      if (Array.isArray(ex.report_types) && ex.report_types.length > 0) {
        // Types already chosen — generate & open directly.
        try {
          const res = await fetch(`${API_BASE_URL}reports`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: `${ex.name} — report`,
              executionkey: ex.executionkey,
              execution_name: ex.name || null,
              report_types: ex.report_types,
              userkey: userkey || null,
            }),
          });
          const body = await res.json().catch(() => ({}));
          if (res.ok && body?.data?.reportkey) {
            window.open(
              `${API_BASE_URL}reports/${encodeURIComponent(body.data.reportkey)}/pdf`,
              "_blank",
              "noopener,noreferrer"
            );
            return;
          }
        } catch {
          /* fall through to show the picker */
        }
      }
      // No saved types or generation failed — show the picker dialog.
      setReportMode("view");
      setReportExecution(ex);
    },
    [userkey]
  );

  // "Edit report types" (pencil icon): always open the picker.
  const handleEditReportTypes = useCallback((ex) => {
    setReportMode("edit");
    setReportExecution(ex);
  }, []);

  // Called by ExecutionReportModal after the user picks types and clicks
  // "Create & view". Saves the chosen types to the execution, then generates
  // the report.
  const handleReportCreated = useCallback(
    async (report, execution, chosenTypes) => {
      // Persist the chosen report_types on the execution for next time.
      if (execution?.executionkey && Array.isArray(chosenTypes) && chosenTypes.length > 0) {
        try {
          await fetch(
            `${API_BASE_URL}executions/${encodeURIComponent(execution.executionkey)}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ report_types: chosenTypes }),
            }
          );
          // Refresh the ticket detail so the execution's report_types are updated
          // in local state (next eye-click will skip the picker).
          if (selectedTicket) refreshTicketDetail(selectedTicket.key);
        } catch {
          /* non-critical — report was already generated */
        }
      }
    },
    [selectedTicket, refreshTicketDetail]
  );

  // --- Render ----------------------------------------------------------------

  // Active create flow: render the existing full-page creation component in
  // place of the tickets table, with the ticket pre-linked and locked so every
  // created case auto-links to it. On finish/cancel we return to the list and
  // refresh the linked-count map.
  if (createFlow) {
    const commonProps = {
      board: createFlow.board,
      subcategorykey: createFlow.subcategorykey,
      presetTickets: createFlow.presetTickets,
      lockTickets: true,
      onCancel: () => setCreateFlow(null),
      onCreate: () => {
        setCreateFlow(null);
        loadLinks();
        setDetailByKey({});
        if (selectedProject && selectedEpic) loadTickets(selectedProject, selectedEpic);
      },
    };
    return createFlow.mode === "ai" ? (
      <CreateTestCasePage {...commonProps} />
    ) : (
      <ManualTestCasePage {...commonProps} />
    );
  }

  // Launched from a ticket: the normal execution wizard, scoped to that ticket
  // with its automated tests pre-selected and the Jira mirror switched on.
  if (execFlow) {
    return (
      <CreateExecutionWizard
        userkey={userkey}
        initialName={execFlow.name}
        initialDescription={execFlow.description}
        initialSelected={execFlow.selected}
        initialLinkTickets={execFlow.linkTickets}
        initialStep={execFlow.step}
        scopeTicketKeys={execFlow.scopeTicketKeys}
        createInXray
        onCancel={() => setExecFlow(null)}
        onCreated={(execution) => {
          const key = execFlow.scopeTicketKeys?.[0];
          setExecFlow(null);
          refreshTicketDetail(key);
          if (execution?.executionkey) onRunExecution?.(execution.executionkey);
        }}
      />
    );
  }

  // The main Tickets landing page: connection banner, filters, ticket grid, and
  // the ticket detail view when one is selected.
  return (
    <div className="space-y-6">
      {status?.connected && selectedTicket && (
        <button
          type="button"
          onClick={() => setSelectedTicket(null)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition hover:text-slate-800"
        >
          <Icons.ArrowLeft className="h-4 w-4" />
          Back to work items
        </button>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Jira Work Items</h1>
          <p className="mt-1 text-sm text-slate-500">
            Pick an epic to load its work items currently in{" "}
            <span className="font-semibold text-blue-700">{STATUS_FILTER}</span>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              loadStatus();
              loadLinks();
              if (selectedProject && selectedEpic) loadTickets(selectedProject, selectedEpic);
            }}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-card px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            <Icons.Refresh className="h-4 w-4" />
            Refresh
          </button>
          <HelpButton screen="tickets" />
        </div>
      </div>

      {/* Connection banner */}
      {statusLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Icons.Spinner className="h-4 w-4 animate-spin" />
          Checking Jira connection…
        </div>
      ) : status?.connected ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          <Icons.Check className="h-4 w-4 flex-none" />
          <span>
            Connected to <span className="font-semibold">{status.base_url}</span>
            {status.display_name ? ` as ${status.display_name}` : ""}
          </span>
        </div>
      ) : (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
          <div className="flex items-center gap-2 font-medium">
            <Icons.X className="h-4 w-4 flex-none" />
            Not connected to Jira
          </div>
          <p className="mt-1 text-red-600">
            {status?.email ||
              "Set JIRA_BASE_URL, JIRA_EMAIL and JIRA_API_TOKEN in the API .env file."}
          </p>
        </div>
      )}

      {/* Controls: project + epic dropdowns + search */}
      {status?.connected && !selectedTicket && (
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <span className="font-medium">Project</span>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              disabled={projectsLoading || !projects.length}
              className="min-w-[14rem] rounded-lg border border-slate-200 bg-card px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-60"
            >
              {projectsLoading && <option>Loading projects…</option>}
              {!projectsLoading && !projects.length && <option>No projects found</option>}
              {projects.map((p) => (
                <option key={p.id || p.key} value={p.key}>
                  {p.name} ({p.key})
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2 text-sm text-slate-600">
            <span className="font-medium">Epic</span>
            <EpicSelect
              value={selectedEpic}
              onChange={setSelectedEpic}
              epics={epics}
              loading={epicsLoading}
            />
          </label>

          <div className="relative flex-1 sm:max-w-xs">
            <Icons.Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by key, summary, assignee…"
              className="w-full rounded-lg border border-slate-200 bg-card py-2 pl-9 pr-3 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Detail page for an opened ticket, else the landing card grid. */}
      {status?.connected && selectedTicket ? (
        <TicketDetail
          ticket={selectedTicket}
          det={detailByKey[selectedTicket.key]}
          count={linkMap[selectedTicket.key]?.testcase_count || 0}
          execCount={linkMap[selectedTicket.key]?.execution_count || 0}
          onCreateTests={() => setCreateTicket(selectedTicket)}
          onLinkTests={() => setManageTicket(selectedTicket)}
          onDelinkTest={(tc) =>
            setConfirmState({
              title: "Delink test",
              message: `Delink test "${tc.name}" from ${selectedTicket.key}? The test case itself is kept.`,
              confirmLabel: "Delink",
              busyLabel: "Delinking…",
              action: () => delinkTest(selectedTicket, tc),
            })
          }
          onCreateExecution={() => startExecution(selectedTicket)}
          onLinkExecution={() => setLinkExecTicket(selectedTicket)}
          onViewReport={(ex) => handleViewReport(ex)}
          onEditReportTypes={(ex) => handleEditReportTypes(ex)}
          onRunExecution={(ex) => onRunExecution?.(ex.executionkey)}
          onDelinkExecution={(ex) =>
            setConfirmState({
              title: "Delink execution",
              message: `Delink execution "${ex.name}" from ${selectedTicket.key}? The execution itself is kept.`,
              confirmLabel: "Delink",
              busyLabel: "Delinking…",
              action: () => delinkExecution(selectedTicket, ex),
            })
          }
          onDeleteExecution={(ex) =>
            setConfirmState({
              title: "Delete execution",
              message: `Delete execution "${ex.name}"? This removes it entirely and cannot be undone.`,
              confirmLabel: "Delete",
              busyLabel: "Deleting…",
              action: () => deleteExecution(selectedTicket, ex),
            })
          }
          onOpenTestCase={onOpenTestCase}
        />
      ) : status?.connected && !selectedEpic ? (
        <div className="rounded-2xl bg-card px-5 py-12 text-center text-sm text-slate-400 ring-1 ring-slate-200">
          Select an epic above to load its {STATUS_FILTER} work items.
        </div>
      ) : status?.connected ? (
        <div className="space-y-3">
          {selectedEpicObj && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="inline-flex items-center gap-1 rounded bg-purple-600/10 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-purple-700">
                <Icons.Boards className="h-3 w-3" />
                Epic
              </span>
              <a
                href={selectedEpicObj.url}
                target="_blank"
                rel="noreferrer"
                className="font-mono font-medium text-brand-700 hover:underline"
              >
                {selectedEpicObj.key}
              </a>
              <span className="text-slate-700">{selectedEpicObj.summary}</span>
              <span className="ml-auto text-slate-400">
                {filteredTickets.length} work item
                {filteredTickets.length === 1 ? "" : "s"} in {STATUS_FILTER}
              </span>
            </div>
          )}

          {ticketsLoading ? (
            <div className="flex items-center gap-2 rounded-2xl bg-card px-5 py-8 text-sm text-slate-500 ring-1 ring-slate-200">
              <Icons.Spinner className="h-4 w-4 animate-spin" />
              Loading {STATUS_FILTER} work items&hellip;
            </div>
          ) : !filteredTickets.length ? (
            <div className="rounded-2xl bg-card px-5 py-10 text-center text-sm text-slate-400 ring-1 ring-slate-200">
              {tickets.length
                ? "No work items match your filter."
                : `No ${STATUS_FILTER} work items under this epic.`}
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3">Key</th>
                    <th className="px-4 py-3">Summary</th>
                    <th className="px-4 py-3">Assignee</th>
                    <th className="px-4 py-3">Priority</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-center">Tests</th>
                    <th className="px-4 py-3 text-center">Execs</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredTickets.map((t) => {
                    const pr = priorityMeta(t.priority);
                    const count = linkMap[t.key]?.testcase_count || 0;
                    const execCount = linkMap[t.key]?.execution_count || 0;
                    return (
                      <tr
                        key={t.key}
                        onClick={() => setSelectedTicket(t)}
                        className="cursor-pointer transition hover:bg-brand-50/50"
                      >
                        <td className="whitespace-nowrap px-4 py-3">
                          <span className="font-mono text-xs font-semibold text-brand-700">
                            {t.key}
                          </span>
                        </td>
                        <td className="max-w-xs px-4 py-3">
                          <span className="line-clamp-1 font-medium text-slate-800">
                            {t.summary}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span className="flex items-center gap-2">
                            <span
                              title={t.assignee || "Unassigned"}
                              className={[
                                "flex h-6 w-6 flex-none items-center justify-center rounded-full text-[10px] font-semibold",
                                t.assignee
                                  ? "bg-brand-600 text-white"
                                  : "bg-slate-200 text-slate-500",
                              ].join(" ")}
                            >
                              {initials(t.assignee)}
                            </span>
                            <span className="text-slate-600">
                              {t.assignee || "Unassigned"}
                            </span>
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span className="inline-flex items-center gap-1.5 text-slate-700">
                            <span
                              className={["h-2 w-2 rounded-full", pr.dot].join(" ")}
                            />
                            {pr.label}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span
                            className={[
                              "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
                              statusBadgeClasses(t.status_category),
                            ].join(" ")}
                          >
                            {t.status || "—"}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-center text-slate-600">
                          {count}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-center text-slate-600">
                          {execCount}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {createTicket && (
        <CreateTestsModal
          ticket={createTicket}
          projectKey={selectedProject}
          onClose={() => setCreateTicket(null)}
          onLaunch={(flow) => {
            setCreateTicket(null);
            setCreateFlow(flow);
          }}
        />
      )}

      {manageTicket && (
        <TicketManageModal
          ticket={manageTicket}
          projectKey={selectedProject}
          onClose={() => setManageTicket(null)}
          onSaved={() => {
            loadLinks();
            setDetailByKey({});
            if (selectedProject && selectedEpic) loadTickets(selectedProject, selectedEpic);
          }}
        />
      )}

      {reportExecution && (
        <ExecutionReportModal
          execution={reportExecution}
          userkey={userkey}
          mode={reportMode}
          onClose={() => setReportExecution(null)}
          onCreated={(report, chosenTypes) => handleReportCreated(report, reportExecution, chosenTypes)}
          onTypesSaved={(chosenTypes) => {
            // Edit mode: types saved, refresh so next eye-click uses them.
            if (selectedTicket) refreshTicketDetail(selectedTicket.key);
            setReportExecution(null);
          }}
        />
      )}

      {linkExecTicket && (
        <LinkExecutionModal
          ticket={linkExecTicket}
          alreadyLinked={
            new Set(
              (detailByKey[linkExecTicket.key]?.executions || []).map(
                (e) => e.executionkey
              )
            )
          }
          onLink={(executionKeys) => linkExecutions(linkExecTicket, executionKeys)}
          onClose={() => setLinkExecTicket(null)}
        />
      )}

      {/* Gated "create execution": while we check the ticket, and when we have
          to refuse. On success this closes and the wizard takes over. */}
      {execGate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className={`flex w-full max-w-lg flex-col overflow-hidden ${DIALOG_HEIGHT} rounded-2xl bg-white shadow-xl ring-1 ring-slate-200`}>
            <div className="flex flex-none items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-slate-900">
                  Create execution
                </h2>
                <p className="mt-0.5 truncate text-sm text-slate-500">
                  <span className="font-mono font-medium text-brand-700">
                    {execGate.ticket.key}
                  </span>{" "}
                  {execGate.ticket.summary}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setExecGate(null)}
                className="flex-none rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <Icons.X className="h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
              {execGate.loading ? (
                <p className="flex items-center gap-2 text-sm text-slate-600">
                  <Icons.Spinner className="h-4 w-4 animate-spin" />
                  Checking this work item's tests and their
                  Artifacts…
                </p>
              ) : (
                <>
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                    <p className="flex items-start gap-2 text-sm font-semibold text-amber-900">
                      <Icons.Alert className="mt-0.5 h-4 w-4 flex-none" />
                      {execGate.ready?.blocked_reason === "no_xray"
                        ? "No tests on this work item"
                        : execGate.ready?.blocked_reason === "no_automation"
                          ? "Nothing automated yet"
                          : "Can't create an execution"}
                    </p>
                    <p className="mt-1 pl-6 text-xs leading-relaxed text-amber-800">
                      {execGate.ready?.message || execGate.message}
                    </p>
                  </div>

                  {(execGate.ready?.not_automated || []).length > 0 && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        Tests waiting to be automated (
                        {execGate.ready.not_automated.length})
                      </p>
                      <ul className="mt-1 space-y-0.5">
                        {execGate.ready.not_automated.map((x) => (
                          <li key={x.key} className="text-[11px] text-slate-600">
                            <span className="font-mono font-semibold text-slate-700">
                              {x.key}
                            </span>{" "}
                            {x.summary}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <p className="text-[11px] leading-snug text-slate-500">
                    An execution needs at least one test with an Artifact
                    behind it — otherwise there's no test step to attach the
                    results and screenshots to, so the run couldn't be reported back
                    to Jira. Use{" "}
                    <span className="font-medium text-slate-700">Create tests</span> on
                    this work item first.
                  </p>
                </>
              )}
            </div>

            <div className="flex flex-none justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <button
                type="button"
                onClick={() => setExecGate(null)}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
              {!execGate.loading && (
                <button
                  type="button"
                  onClick={() => startExecution(execGate.ticket)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                >
                  <Icons.Refresh className="h-3.5 w-3.5" />
                  Re-check
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          busyLabel={confirmState.busyLabel}
          busy={confirmBusy}
          onCancel={() => !confirmBusy && setConfirmState(null)}
          onConfirm={runConfirm}
        />
      )}
    </div>
  );
}
